import { NavLink, Navigate, Route, Routes } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { api } from './api'

import { useAuth } from './auth'
import Board from './pages/Board'
import Catalogs from './pages/Catalogs'
import Groups from './pages/Groups'
import Journal from './pages/Journal'
import Login from './pages/Login'
import Payouts from './pages/Payouts'
import Chat from './pages/Chat'
import Summary from './pages/Summary'
import CodeOrders from './pages/CodeOrders'
import Tickets from './pages/Tickets'
import MobileGate from './mobile/MobileGate'
import { isMobileUI, isPhone, setUIMode } from './mobile/isMobile'

const ROLE_LABEL: Record<string, string> = {
  admin: 'Администратор',
  manager: 'Менеджер',
  specialist: 'Специалист',
}

export default function App() {
  const { user, loading, logout, permissions, can } = useAuth()

  if (loading) return <div className="empty">Загрузка…</div>

  // Телефон получает свою оболочку целиком, а не сжатую настольную: список из
  // двенадцати колонок пальцем не листают. Вход там тоже свой — по короткому коду.
  if (isMobileUI()) return <MobileGate />

  if (!user) return <Login />

  return (
    <div className="app">
      <nav className="sidebar">
        <div className="brand">
          <img src="/brand/logo-mark-white.png" alt="Бизнес Эра" className="brand-logo" />
          <span className="brand-app">Учёт времени</span>
        </div>
        <NavLink to="/journal">Журнал</NavLink>
        <NavLink to="/board">Канбан</NavLink>
        {can('summary.view') && <NavLink to="/summary">Сводка</NavLink>}
        {can('tickets.view') && <ChatLink />}
        {can('tickets.view') && <NavLink to="/tickets">Обращения</NavLink>}
        {can('tickets.view') && <NavLink to="/orders">Заявки</NavLink>}
        {can('tickets.view') && <NavLink to="/code-orders">Заказы кодов</NavLink>}
        {can('payouts.view') && <NavLink to="/payouts">Выплаты</NavLink>}
        <NavLink to="/catalogs">Справочники</NavLink>
        {can('groups.manage') && <NavLink to="/groups">Группы доступа</NavLink>}
        <div className="spacer" />
        <div className="who">
          {user.full_name}
          <br />
          {permissions?.group_name ?? ROLE_LABEL[user.role] ?? user.role}
        </div>
        {isPhone() && (
          // Обратный путь после «Полной версии»: на телефоне настольную вёрстку
          // не пролистать, и без этой кнопки вернуться было бы нечем.
          <button
            className="btn"
            onClick={() => {
              setUIMode('auto')
              window.location.reload()
            }}
          >
            Мобильная версия
          </button>
        )}
        <button className="btn logout" onClick={logout}>
          Выйти
        </button>
      </nav>

      <main className="main">
        <Routes>
          <Route path="/journal" element={<Journal />} />
          <Route path="/board" element={<Board />} />
          {can('tickets.view') && (
            <Route path="/tickets" element={<Tickets kind="request" />} />
          )}
          {can('tickets.view') && <Route path="/orders" element={<Tickets kind="order" />} />}
          {can('tickets.view') && <Route path="/code-orders" element={<CodeOrders />} />}
          {can('tickets.view') && <Route path="/chat" element={<Chat />} />}
          {can('summary.view') && <Route path="/summary" element={<Summary />} />}
          {can('payouts.view') && <Route path="/payouts" element={<Payouts />} />}
          <Route path="/catalogs" element={<Catalogs />} />
          {can('groups.manage') && <Route path="/groups" element={<Groups />} />}
          {/* Адреса кабинета оставляем в покое. Иначе выходило так: сотрудник,
              вошедший в программу, открывает ссылку на кабинет — этот перехват
              срабатывает первым, переписывает адрес на журнал, и до кабинета
              человек не доезжает никогда. */}
          <Route path="/portal/*" element={null} />
          <Route path="*" element={<Navigate to="/journal" replace />} />
        </Routes>
      </main>
    </div>
  )
}


/**
 * Пункт «Сообщения» с числом непрочитанного.
 *
 * Отдельным компонентом, чтобы запрос счётчика не перерисовывал всё меню
 * каждые полминуты.
 */
function ChatLink() {
  const unread = useQuery({
    queryKey: ['chat-unread'],
    queryFn: api.unreadChat,
    refetchInterval: 30_000,
  })
  const count = unread.data?.unread ?? 0
  return (
    <NavLink to="/chat">
      Сообщения
      {count > 0 && <span className="ms-badge">{count}</span>}
    </NavLink>
  )
}
